import { apiClient } from '@/lib/api-client';
import { getErrorMessage } from '@/types/errors';
import ErrorMessage from '@components/ErrorMessage';
import { getTextClassName } from '@nautilusbio/nb-design-system';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack, getStackClassName } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm } from '@tanstack/react-form';
import { useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import React, { type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import loginStyles from '../Login/styles.module.css';

import { makeResetPasswordFormSchema, resetPasswordFailure } from './resetLink';
import styles from './styles.module.css';

// Helper to build login URL with params for TanStack Router
const getLoginUrl = (loginEmail: string) => ({
  to: '/login',
  search: { email: loginEmail, mode: 'password' as const },
  replace: true,
});

const ResetPasswordPage: FC = () => {
  const { t } = useTranslation('login');
  const search = useSearch({ from: '/reset-password/' });
  const navigate = useNavigate();

  const email = search.email;
  const code = search.code;

  const defaultValues = {
    code,
    email,
    new_password: '',
  };

  const formSchema = useMemo(() => makeResetPasswordFormSchema(t), [t]);

  const form = useForm({
    defaultValues,
    validators: {
      onChange: formSchema,
    },
    onSubmit: async ({ value }) => {
      const toastId = toast.loading(t('form.labels.resetting_password'));
      const returnToLogin = () => setTimeout(() => navigate(getLoginUrl(value.email)), 2000);

      try {
        await apiClient.post('/auth/reset-password', {
          email: value.email,
          code: String(value.code),
          new_password: value.new_password,
        });
        toast.success(t('form.labels.reset_password_success'), { id: toastId });
        returnToLogin();
      } catch (error) {
        const { messageKey, linkSpent } = resetPasswordFailure(getErrorMessage(error));
        toast.error(t(messageKey), { id: toastId });
        // A rejected password leaves the reset code valid, so keep the user on the form —
        // leaving would discard a link they can still use.
        if (linkSpent) returnToLogin();
      }
    },
  });

  useEffect(() => {
    if (search.email) {
      form.setFieldValue('email', search.email);
    }
    if (search.code) {
      form.setFieldValue('code', search.code);
    }
  }, [form, search]);

  return (
    <Stack alignItems="center" fullHeight fullWidth justifyContent="center">
      <Stack alignItems="center" justifyContent="start" orientation="vertical" gap={4}>
        <form
          className={clsx(loginStyles.form, styles.form)}
          onSubmit={async e => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <Stack fullWidth gap={3} orientation="vertical">
            <Text as="h1" className={clsx(styles.title, styles.h1title)}>
              {t('form.labels.reset_password')}
            </Text>
            <Text as="h3" className={clsx(styles.title, getStackClassName(['displayflex', 'aicenter']))}>
              {t('form.labels.email')}:&nbsp;&nbsp;
              <Text as="span" className={getTextClassName('sm')}>
                {email}
              </Text>
            </Text>

            <form.Field name="email">
              {field => <input type="hidden" name="email" value={field.state.value} />}
            </form.Field>
            <form.Field name="code">
              {field => <input type="hidden" name="code" value={field.state.value} />}
            </form.Field>

            <form.Field name="new_password">
              {field => (
                <>
                  <Input
                    data-testid="reset-password-input"
                    fullWidth
                    label={{
                      children: t('form.labels.new_password'),
                      htmlFor: field.name,
                      id: 'new-password-label',
                    }}
                    onChange={e => field.handleChange(e.target.value)}
                    required
                    type="password"
                    value={field.state.value}
                    id={field.name}
                  />
                  <form.Subscribe
                    selector={state => [state.fieldMeta.new_password?.errors, state.fieldMeta.new_password?.isDirty]}
                  >
                    {([errors, isDirty]) =>
                      isDirty && Array.isArray(errors) && errors.length > 0
                        ? errors.map((error, i) => <ErrorMessage error={error} key={`error-${i}`} />)
                        : null
                    }
                  </form.Subscribe>
                </>
              )}
            </form.Field>

            <form.Subscribe
              selector={state => [
                state.values.code,
                state.values.email,
                state.values.new_password,
                state.isValid,
                state.isSubmitting,
              ]}
            >
              {([codeVal, emailVal, new_password, isValid, isSubmitting]) => (
                <Button
                  className={styles.submit}
                  data-testid="reset-password-submit"
                  isDisabled={!isValid || !codeVal || !emailVal || !new_password}
                  isPending={!!isSubmitting}
                  type="submit"
                  visual="primary"
                >
                  {t('form.labels.reset_password')}
                </Button>
              )}
            </form.Subscribe>
          </Stack>
        </form>
      </Stack>
    </Stack>
  );
};

export default ResetPasswordPage;
